import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { Grid, Card, CardHeader, CardContent, Stepper, StepLabel, Step, Breadcrumbs, Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import type { Theme } from '@mui/material/styles';
import { useCountriesStore, useNationalitiesStore } from '@lib/store';
import { SupplementaryApplicationEntity, ApplicationApi, ApiUtils } from '@shared/api';
import { useRouteHook } from '@shared/hooks';
import { RoutePaths } from '@shared/utils';
import { ISupplementaryApplicationFormStep1, ISupplementaryApplicationFormStep2, TransformData } from './utils';
import {
  StepAnticipatedMonthlySpend,
  StepApplicant,
  StepDataProtection,
  StepMainCardholder,
  StepPEP,
  StepSecurityQuestions,
  StepSupplementaryContact,
} from './widgets';
import { SnackbarContext } from '@widgets';
import { Button, DelayedLinearProgress } from '@shared/widgets';

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    width: '100%',
  },
  button: {
    marginRight: theme.spacing(1),
  },
  completed: {
    display: 'inline-block',
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  dropArea: {
    textAlign: 'center',
    padding: theme.spacing(7),
  },
  saveIndicator: {
    left: 0,
    position: 'fixed',
    top: 0,
    width: '100%',
  },
}));

type Params = {
  selectedApplicationId: string;
  supplementaryApplicationId?: string;
};

export const SupplementaryApplicationFormPage = () => {
  const { selectedApplicationId, supplementaryApplicationId } = useParams<Params>();
  const { navigate } = useRouteHook();

  const { data: countries, getCountries } = useCountriesStore();
  const { data: nationalities, getNationalities } = useNationalitiesStore();

  const [loadingApplication, setLoadingApplication] = useState<boolean>(false);
  const [mainApplicationName, setMainApplicationName] = useState<string>('Loading...');
  const [applicationData, setApplicationData] = useState<SupplementaryApplicationEntity>(TransformData.defaultSupplementaryApplication);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);

  const snackbar = useContext(SnackbarContext);
  const classes = useStyles();

  const methods = useForm<ISupplementaryApplicationFormStep1>({ defaultValues: TransformData.defaultSupplementaryApplicationFormStep1 });
  const methods2 = useForm<ISupplementaryApplicationFormStep2>({ defaultValues: TransformData.defaultSupplementaryApplicationFormStep2 });

  useEffect(() => {
    getCountries();
    getNationalities();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoadingApplication(true);
        const mainApplicationId: string = selectedApplicationId!;
        const applicationId: string | undefined = supplementaryApplicationId;

        const [supplementaryApplication, mainApplication] = await Promise.all([
          applicationId && applicationId !== 'new' ? ApplicationApi.getSupplementaryApplication(applicationId) : Promise.resolve(null),
          ApplicationApi.getMainApplication(mainApplicationId),
        ]);

        if (mainApplication?.name) setMainApplicationName(mainApplication.name);
        setApplicationData((prev) => {
          const data = {
            ...prev,
            ...TransformData.transformSupplementaryApplication({ mainApplicationId, application: mainApplication, supplementaryApplication }),
          };
          methods.reset(TransformData.transformSupplementaryApplicationFormStep1(data));
          methods2.reset(TransformData.transformSupplementaryApplicationFormStep2(data));
          return data;
        });
      } catch (err) {
        snackbar.error('An error has occurred when obtaining main cardholder details: ' + ApiUtils.getError(err));
      } finally {
        setLoadingApplication(false);
      }
    })();
  }, [selectedApplicationId, supplementaryApplicationId]);

  const steps = [
    {
      title: 'Applicant Details',
      content: (
        <FormProvider {...methods}>
          <Card className={classes.card}>
            <CardHeader title='Main Cardholder Details' />
            <CardContent>
              <StepMainCardholder />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='Supplementary Card Applicant Details' />
            <CardContent>
              <StepApplicant countries={countries} nationalities={nationalities} />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='Contact Details' />
            <CardContent>
              <StepSupplementaryContact countries={countries} />
            </CardContent>
          </Card>

          <Card className={classes.card}>
            <CardHeader title='Anticipated Monthly Spend' />
            <CardContent>
              <StepAnticipatedMonthlySpend />
            </CardContent>
          </Card>

          <Card className={classes.card}>
            <CardHeader title='Politically Exposed Persons (PEP) Declaration' />
            <CardContent>
              <StepPEP />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='Security Question' />
            <CardContent>
              <StepSecurityQuestions />
            </CardContent>
          </Card>
        </FormProvider>
      ),
    },
    {
      title: 'Data Protection',
      content: (
        <FormProvider {...methods2}>
          <Card className={classes.card}>
            <CardHeader title='Data Protection and Declaration' />
            <StepDataProtection />
          </Card>
        </FormProvider>
      ),
    },
  ];

  const onSubmitFormStep1 = async (data: ISupplementaryApplicationFormStep1) => {
    const newActiveStep = activeStepIndex + 1;
    setApplicationData((prev) => ({ ...prev, ...data }) as SupplementaryApplicationEntity);
    setActiveStepIndex(newActiveStep);
  };
  const onSubmitFormStep2 = async (data: ISupplementaryApplicationFormStep2) => {
    try {
      const newActiveStep = activeStepIndex + 1;
      if (newActiveStep >= steps.length) {
        setProcessing(true);
        await saveSupplementaryApplication({ ...applicationData, ...data } as SupplementaryApplicationEntity, true);
      }
    } catch (error) {
      snackbar.error(ApiUtils.getError(error));
    } finally {
      setProcessing(false);
    }
  };
  const handleSave = async (applicationData: SupplementaryApplicationEntity) => {
    try {
      setSaving(true);
      const isValid = await handleValidate();
      if (isValid) await saveSupplementaryApplication({ ...applicationData, acceptedTC: true });
    } finally {
      setSaving(false);
    }
  };
  const handleBack = () => {
    window.scrollTo(0, 0);
    setActiveStepIndex(activeStepIndex - 1);
  };
  const handleValidate = async () => {
    const isValidStep1 = await methods.trigger();
    const isValidStep2 = methods2.getValues('acceptedTC');

    if (isValidStep1 && isValidStep2) {
      snackbar.success('Application form is valid');
    } else if (!isValidStep2) {
      snackbar.error('You should accept Terms and Conditions');
    }

    return isValidStep1 && isValidStep2;
  };
  const saveSupplementaryApplication = async (applicationData: SupplementaryApplicationEntity, submit?: boolean) => {
    try {
      const applicationId: string = await ApplicationApi.saveSupplement(applicationData);
      if (submit) {
        await ApplicationApi.submitApplication(applicationId);
        snackbar.success('Application has successfully submitted');
      } else {
        snackbar.success('Application has successfully saved');
      }
      setSaving(true);
      navigate(RoutePaths.applicationsSupplementaryDetails.onNavigate(applicationData!.mainAppId, applicationId));
    } catch (error) {
      setSaving(false);
      snackbar.error(ApiUtils.getError(error));
    }
  };

  return (
    <>
      <Breadcrumbs aria-label='breadcrumb' sx={{ marginBottom: '12px' }}>
        <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='hover' color='inherit' href={RoutePaths.applications.onNavigate()}>
          Applications
        </Link>
        {applicationData && (
          <Link
            sx={{ textTransform: 'uppercase', fontSize: '12px' }}
            underline='hover'
            color='inherit'
            href={RoutePaths.applicationsDetails.onNavigate(applicationData.mainAppId)}
          >
            {mainApplicationName}
          </Link>
        )}
        {applicationData && (
          <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='none'>
            {applicationData.name ? applicationData.name : 'New Supplementary Application'}
          </Link>
        )}
      </Breadcrumbs>
      <Grid container alignContent='center'>
        <Grid item xs={12}>
          <DelayedLinearProgress loading={loadingApplication && !saving && !processing} />
          <Stepper nonLinear activeStep={activeStepIndex}>
            {steps.map((step, idx) => (
              <Step key={idx} completed={idx < activeStepIndex}>
                <StepLabel onClick={() => setActiveStepIndex(idx)}>{step.title}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>
        {!loadingApplication && (
          <Grid item xs={12}>
            <form onSubmit={activeStepIndex === 0 ? methods.handleSubmit(onSubmitFormStep1) : methods2.handleSubmit(onSubmitFormStep2)}>
              {steps[activeStepIndex]?.content ?? null}
              <div>
                <Button className={classes.button} disabled={activeStepIndex === 0 || saving} onClick={handleBack}>
                  Back
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  className={classes.button}
                  type='submit'
                  loading={saving}
                  onClick={() => handleSave(applicationData)}
                >
                  Save
                </Button>
                {activeStepIndex < steps.length - 1 && (
                  <Button variant='contained' color='primary' className={classes.button} type='submit' loading={processing}>
                    Next
                  </Button>
                )}
                <Button type='button' variant='contained' color='primary' className={classes.button} onClick={handleValidate}>
                  Validate
                </Button>
              </div>
            </form>
          </Grid>
        )}
      </Grid>
    </>
  );
};
